export type TNormalizedStickerPlacement = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TOutputPixelRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TCanvasSmoothingQuality = "default" | "low" | "medium" | "high";

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const getStickerOutputPixelRect = ({
  placement,
  canvasWidth,
  canvasHeight,
  alignToPixels = false,
}: {
  placement: TNormalizedStickerPlacement;
  canvasWidth: number;
  canvasHeight: number;
  alignToPixels?: boolean;
}): TOutputPixelRect => {
  const left = clamp(placement.x * canvasWidth, 0, canvasWidth);
  const top = clamp(placement.y * canvasHeight, 0, canvasHeight);
  const right = clamp(
    (placement.x + placement.width) * canvasWidth,
    0,
    canvasWidth,
  );
  const bottom = clamp(
    (placement.y + placement.height) * canvasHeight,
    0,
    canvasHeight,
  );

  if (!alignToPixels) {
    return {
      x: left,
      y: top,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
    };
  }

  const alignedLeft = Math.round(left);
  const alignedTop = Math.round(top);
  const alignedRight = Math.max(
    alignedLeft + 1,
    Math.min(canvasWidth, Math.round(right)),
  );
  const alignedBottom = Math.max(
    alignedTop + 1,
    Math.min(canvasHeight, Math.round(bottom)),
  );

  return {
    x: alignedLeft,
    y: alignedTop,
    width: Math.max(0, alignedRight - alignedLeft),
    height: Math.max(0, alignedBottom - alignedTop),
  };
};

export const configureCanvasImageSmoothing = (
  context: CanvasRenderingContext2D,
  quality: TCanvasSmoothingQuality,
) => {
  if (quality === "default") return;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = quality;
};

export const drawStickerPlacements = ({
  context,
  stickerImage,
  placements,
  canvasWidth,
  canvasHeight,
  smoothingQuality = "default",
  alignToPixels = false,
}: {
  context: CanvasRenderingContext2D;
  stickerImage: CanvasImageSource;
  placements: TNormalizedStickerPlacement[];
  canvasWidth: number;
  canvasHeight: number;
  smoothingQuality?: TCanvasSmoothingQuality;
  alignToPixels?: boolean;
}) => {
  configureCanvasImageSmoothing(context, smoothingQuality);

  return placements.map((placement) => {
    const rectangle = getStickerOutputPixelRect({
      placement,
      canvasWidth,
      canvasHeight,
      alignToPixels,
    });
    if (rectangle.width > 0 && rectangle.height > 0) {
      context.drawImage(
        stickerImage,
        rectangle.x,
        rectangle.y,
        rectangle.width,
        rectangle.height,
      );
    }
    return rectangle;
  });
};
